import DocumentDetails from '@/modules/common/hiring/models/APP/DocumentDetails'
import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
import UserDetails from '@/modules/common/hiring/models/APP/UserDetails'
import AdditionalCoverage from '@/modules/common/common/models/APP/AdditionalCoverage'
import { UserRoleEnum } from '@/modules/core/models/enums'

export default class Insured {
  private _riskNumber!: number
  private _role!: UserRoleEnum

  private _additionalCoverages!: AdditionalCoverage[]
  private _isFirstInsured!: boolean

  private _documentDetails = new DocumentDetails()
  private _extraDocument?: DocumentDetails
  private _addressDetails = new AddressDetails()
  private _userDetails = new UserDetails()

  private _thirdPartiesAnswered!: boolean
  private _thirdPartyWrittenAdvertising!: boolean | null
  private _thirdPartyTelephoneAdvertising!: boolean | null
  private _dataTransfer!: boolean | null
  private _preContractualDocumentChecked!: boolean

  private _privacyPolicyConsent!: boolean
  private _asisaNewsConsent!: boolean
  private _relationship?: string
  private _hasHealthPolicy?: string | undefined

  private _declaration?: boolean | undefined

  private _healthPolicyNumber?: string

  private _policyHolderConsent?: boolean

  constructor(data: any = {}) {
    this.riskNumber = data.riskNumber
    this.role = data.role
    this.isFirstInsured = data.isFirstInsured
    this.additionalCoverages = data.additionalCoverages
    this.documentDetails = data.documentDetails
    this.extraDocument = data.extraDocument
    this.addressDetails = data.addressDetails
    this.userDetails = data.userDetails
    this.relationship = data.relationship

    // Solo para el holder (no se envia al back)
    this.preContractualDocumentChecked = data.preContractualDocumentChecked

    // Solo para el holder
    this.thirdPartiesAnswered = false
    // Solo para el holder
    this.thirdPartyTelephoneAdvertising = data.thirdPartyTelephoneAdvertising
    // Solo para el holder
    this.thirdPartyWrittenAdvertising = data.thirdPartyWrittenAdvertising
    // Solo para el holder
    this.privacyPolicyConsent = data.privacyPolicyConsent
    // ? no se usa
    this.dataTransfer = data.dataTransfer
    // ? no se usa
    this.asisaNewsConsent = data.asisaNewsConsent

    this.hasHealthPolicy = data.hasHealthPolicy
    this.declaration = data.declaration
    this.healthPolicyNumber = data.healthPolicyNumber
    this.policyHolderConsent = data.policyHolderConsent
  }

  get riskNumber(): number {
    return this._riskNumber
  }

  set riskNumber(value: number | undefined) {
    this._riskNumber = value ?? 0
  }

  get isHolder(): boolean {
    return (
      this.role === UserRoleEnum.HOLDER_INSURED || this.role === UserRoleEnum.HOLDER_NOT_INSURED
    )
  }

  get role(): UserRoleEnum {
    return this._role
  }
  set role(value: UserRoleEnum | undefined) {
    this._role = value ?? UserRoleEnum.INSURED
  }

  get isFirstInsured(): boolean {
    return this._isFirstInsured
  }
  set isFirstInsured(value: boolean | undefined) {
    this._isFirstInsured = value ?? false
  }

  get additionalCoverages(): AdditionalCoverage[] {
    return this._additionalCoverages
  }
  set additionalCoverages(value: AdditionalCoverage[] | undefined) {
    this._additionalCoverages = value?.map((a) => new AdditionalCoverage(a)) ?? []
  }

  get documentDetails(): DocumentDetails {
    return this._documentDetails
  }
  set documentDetails(value: DocumentDetails | null | undefined) {
    this._documentDetails = new DocumentDetails({ ...this._documentDetails, ...(value ?? {}) })
  }

  get extraDocument(): DocumentDetails | undefined {
    return this._extraDocument
  }
  set extraDocument(value: DocumentDetails | null) {
    this._extraDocument = new DocumentDetails({ ...this._extraDocument, ...(value ?? {}) })
  }

  get addressDetails(): AddressDetails {
    return this._addressDetails
  }
  set addressDetails(value: AddressDetails | null | undefined) {
    this._addressDetails = new AddressDetails({ ...this._addressDetails, ...(value ?? {}) })
  }

  get userDetails(): UserDetails {
    return this._userDetails
  }
  set userDetails(value: Partial<UserDetails> | null | undefined) {
    this._userDetails = new UserDetails({ ...this._userDetails, ...(value ?? {}) })
  }

  get thirdPartiesAnswered(): boolean {
    return this._thirdPartiesAnswered
  }
  set thirdPartiesAnswered(value: boolean | undefined) {
    this._thirdPartiesAnswered = value ?? false
  }

  get thirdPartyTelephoneAdvertising(): boolean | null {
    return this._thirdPartyTelephoneAdvertising
  }
  set thirdPartyTelephoneAdvertising(value: boolean | null | undefined) {
    this._thirdPartyTelephoneAdvertising = value ?? null
  }

  get thirdPartyWrittenAdvertising(): boolean | null {
    return this._thirdPartyWrittenAdvertising
  }
  set thirdPartyWrittenAdvertising(value: boolean | null | undefined) {
    this._thirdPartyWrittenAdvertising = value ?? null
  }

  get dataTransfer(): boolean | null {
    return this._dataTransfer
  }
  set dataTransfer(value: boolean | null | undefined) {
    this._dataTransfer = value ?? null
  }

  get preContractualDocumentChecked(): boolean {
    return this._preContractualDocumentChecked
  }
  set preContractualDocumentChecked(value: boolean | undefined) {
    this._preContractualDocumentChecked = value ?? false
  }

  get privacyPolicyConsent(): boolean {
    return this._privacyPolicyConsent
  }
  set privacyPolicyConsent(value: boolean | undefined) {
    this._privacyPolicyConsent = value ?? false
  }
  get asisaNewsConsent(): boolean {
    return this._asisaNewsConsent
  }
  set asisaNewsConsent(value: boolean | undefined) {
    this._asisaNewsConsent = value ?? false
  }

  get relationship(): string | undefined {
    return this._relationship
  }

  set relationship(value: string | undefined) {
    this._relationship = value
  }

  get hasHealthPolicy(): string | undefined {
    return this._hasHealthPolicy
  }

  set hasHealthPolicy(value: string | undefined) {
    this._hasHealthPolicy = value
  }

  get declaration(): boolean | undefined {
    return this._declaration
  }

  set declaration(value: boolean | undefined) {
    this._declaration = value
  }

  get healthPolicyNumber(): string | undefined {
    return this._healthPolicyNumber
  }

  set healthPolicyNumber(value: string | undefined) {
    this._healthPolicyNumber = value
  }

  get policyHolderConsent(): boolean | undefined {
    return this._policyHolderConsent
  }

  set policyHolderConsent(value: boolean | undefined) {
    this._policyHolderConsent = value
  }

  updateAttributes(newAttributes: Partial<Insured>) {
    Object.assign(this, newAttributes)
  }
}
